import { generateId } from '@bn-games/history-core';
import type { GameState } from '../game/GameState.js';
import { buildRecord } from './record.js';
import type { GameRecord, RecordedClocks, RecordedSeed } from './types.js';

/**
 * Records a local game (bot or hot-seat). Nothing is persisted while it is
 * played — a local game does not survive a reload — so this only collects
 * what the final `GameState` cannot give back: the clocks after each move.
 */
export class LocalGameLog {
  private clocks: (RecordedClocks | null)[] = [];
  private createdAt: number;
  private reported = false;

  constructor(
    private readonly seed: Omit<RecordedSeed, 'createdAt'>,
    private readonly onFinished: (record: GameRecord) => void,
    private readonly now: () => number = Date.now,
  ) {
    this.createdAt = now();
  }

  /** A move was played; `clocks` is the reading right after it. */
  moved(state: GameState, clocks: RecordedClocks | null): void {
    // An imported game arrives with moves already made: they have no reading.
    while (this.clocks.length < state.moves.length - 1) this.clocks.push(null);
    this.clocks.push(clocks === null ? null : { w: clocks.w, b: clocks.b });
    this.changed(state);
  }

  /** Any other change: the game may have ended off the board, or been restarted. */
  changed(state: GameState): void {
    if (state.outcome === null) {
      // Play goes on after a reported game, or with fewer moves than were logged: a restart.
      if (this.reported || state.moves.length < this.clocks.length) this.reset();
      return;
    }
    if (this.reported) return;
    this.reported = true;
    const record = buildRecord({
      id: generateId(),
      seed: { ...this.seed, createdAt: this.createdAt },
      state,
      clocks: this.clocks,
      finishedAt: this.now(),
    });
    if (record !== null) this.onFinished(record);
  }

  private reset(): void {
    this.clocks = [];
    this.createdAt = this.now();
    this.reported = false;
  }
}
